/**
 * SPA entry — deliberately tiny.
 *
 * Boot order:
 *   1. runtime config (public/config.json) and the ACTIVE locale's catalog load
 *      in parallel. English ships in this entry chunk; French / Arabic are lazy
 *      chunks fetched only for visitors who use them (see src/i18n/locales).
 *   2. only then is the application graph (`@/app/mount`) imported and
 *      evaluated — modules that compute `tr()` copy at import time therefore see
 *      the right catalog, and the first render is already in the detected
 *      language + direction. Until then index.html shows a neutral boot spinner.
 */

import { loadConfig } from '@/config';
import { initI18n } from '@/i18n';

import './index.css';

/**
 * Auto-recover from stale-deploy chunk errors. When a new build is deployed, its
 * content-hashed chunks replace the old ones; an ALREADY-OPEN session still holds
 * the old chunk names, so the next lazy route import 404s → Vite emits
 * `vite:preloadError`. Instead of dead-ending on "Failed to fetch dynamically
 * imported module", reload once to fetch the fresh index.html + chunks. The
 * sessionStorage timestamp guards against a reload loop if the reload also fails.
 */
function reloadOnceForStaleChunks(): void {
  const KEY = 'chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) || '0');
  if (Date.now() - last > 10_000) {
    sessionStorage.setItem(KEY, String(Date.now()));
    window.location.reload();
  }
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadOnceForStaleChunks();
});

async function bootstrap() {
  // Runtime config must be available before anything renders (auth, API); the
  // active locale's catalog must be in memory before the app modules evaluate.
  await Promise.all([loadConfig(), initI18n()]);

  let mount: typeof import('@/app/mount');
  try {
    mount = await import('@/app/mount');
  } catch (err) {
    // The app chunk itself could not be fetched (stale deploy / network blip).
    console.error('[boot] could not load the application bundle', err);
    reloadOnceForStaleChunks();
    return;
  }
  mount.mountApp();
}

void bootstrap();
